import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Lock, Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';
import EpmTooltip from './EpmTooltip';
import { useEPMContext } from '../../../contexts/EPMContext';
import { useUserContext } from '../../../contexts/UserContext';
import { permission } from '../../../config/permission';
import { durationCurve, durationDays, requiredSampleSize } from '../../../utils/epmStats';
import { formatTestCardId } from '../../../utils/converter';
import { findLocationFieldMatches, structureOf, testId } from '../epmHelpers';
import { findMetric, manualMetric } from '../metricsCatalog';
import MetricPicker from './MetricPicker';
import useAutoSave from './useAutoSave';
import { EPM_LIMITS } from '../validation/epmRules';
import { primaryMetricFormatSchema, secondaryMetricItemSchema, createValidator } from '../validation/epmSchemas';
import EpmCharCounter from '../EpmCharCounter';
import useEpmValidation from '../validation/useEpmValidation';
import { Input, Select } from '../../UI';

const MetricChip = ({ children }) => (
    <span className="epm-plan__chip">
        {children}
        <X size={11} className="cursor-pointer ml-1 text-[#a1a1aa]" />
    </span>
);

const MIN_MDE = 1;
const MAX_MDE = 10;
const MIN_RUN_DAYS = 7;
const VIABILITY_LIMIT_DAYS = 28;
const SLIDER_LABELS = [1, 2, 3, 5, 10];
const MAX_SECONDARY_METRICS = 5;

const toSecondaryMetricsList = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) {
        return val.filter((item) => typeof item === 'string' && item.trim().length > 0);
    }
    if (typeof val === 'string' && val.trim().length > 0) {
        return [val.trim()];
    }
    return [];
};

const ALPHA_OPTIONS = { 'α 0.05': 0.05, 'α 0.01': 0.01 };
const POWER_OPTIONS = { 'Power 80%': 0.8, 'Power 90%': 0.9 };
const TAILS_OPTIONS = { 'Two-sided': 2, 'One-sided': 1 };
const ALPHA_SELECT_OPTIONS = Object.keys(ALPHA_OPTIONS).map((o) => ({ value: o, label: o }));
const POWER_SELECT_OPTIONS = Object.keys(POWER_OPTIONS).map((o) => ({ value: o, label: o }));
const TAILS_SELECT_OPTIONS = Object.keys(TAILS_OPTIONS).map((o) => ({ value: o, label: o }));

const sliderPosition = (mde) => ((mde - MIN_MDE) / (MAX_MDE - MIN_MDE)) * 100;
const formatNumber = (value) => value.toLocaleString('en-GB');

// Chart geometry (viewBox 720 × 190): x is linear in MDE, y in days
const X0 = 45;
const X1 = 690;
const Y0 = 160;
const Y1 = 25;
const xFor = (mde) => X0 + ((mde - MIN_MDE) / (MAX_MDE - MIN_MDE)) * (X1 - X0);

const DurationChart = ({ curve, effect, days, label }) => {
    const yMax = Math.ceil(curve[0].days / 50) * 50;
    const yFor = (value) => Y0 - (Math.min(value, yMax) / yMax) * (Y0 - Y1);
    const path = curve.map((point, index) => `${index ? 'L' : 'M'}${xFor(point.mde).toFixed(1)} ${yFor(point.days).toFixed(1)}`).join(' ');

    const x = xFor(effect);
    const y = yFor(days);
    const half = effect / 2;
    const halfPoint = half >= MIN_MDE ? curve.find((point) => Math.abs(point.mde - half) < 0.05) : null;
    const labelOnLeft = x > 520;

    return (
        <div className="epm-plan__chart" aria-label="Test duration curve">
            <svg viewBox="0 0 720 190" role="img">
                <title>Estimated test duration decreases as minimum detectable effect increases</title>
                <line x1={X0} y1={Y0} x2={X1} y2={Y0} className="axis" /><line x1={X0} y1={Y1} x2={X0} y2={Y0} className="axis" />
                <line x1={X0} y1={yFor(VIABILITY_LIMIT_DAYS)} x2={X1} y2={yFor(VIABILITY_LIMIT_DAYS)} stroke="#e0a64a" strokeDasharray="3 5" strokeWidth="1" />
                <text x={X1 + 2} y={yFor(VIABILITY_LIMIT_DAYS) - 4}>4 wk</text>
                <line x1={x} y1={Y1} x2={x} y2={Y0} className="guide" />
                <path d={path} className="curve" />
                {halfPoint && (
                    <>
                        <circle cx={xFor(halfPoint.mde)} cy={yFor(halfPoint.days)} r="3.5" fill="#8d97b1" />
                        <text x={xFor(halfPoint.mde) + 9} y={Math.max(Y1 + 12, yFor(halfPoint.days) - 6)} className="note">
                            {halfPoint.mde}% · {halfPoint.days} days
                        </text>
                    </>
                )}
                <circle cx={x} cy={y} r="6" className="point" />
                <text x={labelOnLeft ? x - 12 : x + 12} y="55" textAnchor={labelOnLeft ? 'end' : 'start'} className="value">{label}</text>
                <text x="10" y={Y1 + 8}>{yMax}d</text><text x="14" y={(Y0 + Y1) / 2 + 3}>{yMax / 2}d</text><text x="30" y={Y0 + 3}>0</text>
                {SLIDER_LABELS.map((mde) => <text key={mde} x={xFor(mde) - 6} y="181">{mde}%</text>)}
            </svg>
        </div>
    );
};

const PlanStep = ({ testData, setTestData }) => {
    const { tests, testLocations = [] } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const { saveNow, saveLater, flush } = useAutoSave(testData);
    const { fieldErrors, validateFieldValue } = useEpmValidation();

    const savedPlan = structureOf(testData).plan || {};

    const [effect, setEffect] = useState(savedPlan.mde || 3);
    const [stopRule, setStopRule] = useState(savedPlan.stop_rule || 'fixed');
    const [primaryMetric, setPrimaryMetric] = useState(savedPlan.primary_metric || '');
    const [conflictResolution, setConflictResolution] = useState(savedPlan.conflict_resolution || null);
    const [conflictsOpen, setConflictsOpen] = useState(true);
    // const [feasibilityOpen, setFeasibilityOpen] = useState(false);
    // const [blockerOpen, setBlockerOpen] = useState(true);

    // The chosen metric supplies the baseline and eligible traffic for the calculation
    const metric = findMetric(primaryMetric) || manualMetric(primaryMetric, savedPlan);

    const [alpha, setAlpha] = useState('α 0.05');
    const [power, setPower] = useState('Power 80%');
    const [tails, setTails] = useState('Two-sided');

    // One calculation drives the headline, chart, sample size and gate (01 §5.3)
    const stats = useMemo(() => {
        const inputs = {
            baseline: metric.baseline,
            alpha: ALPHA_OPTIONS[alpha],
            power: POWER_OPTIONS[power],
            tails: TAILS_OPTIONS[tails],
        };
        const perVariant = requiredSampleSize({ ...inputs, mde: effect / 100 });
        const rawDays = durationDays({ perVariant, dailyTraffic: metric.dailyTraffic });
        // A third arm compares twice against control, so α is Šidák-corrected
        const sidakAlpha = 1 - (1 - inputs.alpha) ** (1 / 2);
        const thirdArmPerVariant = requiredSampleSize({ ...inputs, alpha: sidakAlpha, mde: effect / 100 });
        return {
            perVariant,
            rawDays,
            days: Math.max(MIN_RUN_DAYS, rawDays),
            thirdArmDays: durationDays({ perVariant: thirdArmPerVariant, dailyTraffic: metric.dailyTraffic, variants: 3 }),
            curve: durationCurve({ ...inputs, dailyTraffic: metric.dailyTraffic, from: MIN_MDE, to: MAX_MDE, step: 0.25 }),
        };
    }, [effect, alpha, power, tails, metric.baseline, metric.dailyTraffic]);

    // Keep the plan on the test so the peek, board and later steps can read it.
    // Clicks save straight away; the effect slider saves once it stops moving.
    const savePlan = (changes, structureChanges = {}, { debounced = false } = {}) => {
        setTestData((prev) => {
            const ideation = prev?.ideation || {};
            const structure = ideation.structure || {};
            return {
                ...prev,
                ideation: {
                    ...ideation,
                    structure: { ...structure, ...structureChanges, plan: { ...(structure.plan || {}), ...changes } },
                },
            };
        });
        if (debounced) {
            saveLater('ideation.structure', (test) => ({ ideation: { structure: test?.ideation?.structure || {} } }));
        } else {
            const current = testData?.ideation?.structure || {};
            const nextStructure = { ...current, ...structureChanges, plan: { ...(current.plan || {}), ...changes } };
            saveNow({ ideation: { structure: nextStructure } }, 'ideation.structure');
        }
    };

    const daysFor = (mde, chosen) => {
        const perVariant = requiredSampleSize({
            baseline: chosen.baseline, mde: mde / 100,
            alpha: ALPHA_OPTIONS[alpha], power: POWER_OPTIONS[power], tails: TAILS_OPTIONS[tails],
        });
        return Math.max(MIN_RUN_DAYS, durationDays({ perVariant, dailyTraffic: chosen.dailyTraffic }));
    };

    const changeEffect = (value) => {
        setEffect(value);
        savePlan({ mde: value, duration_days: daysFor(value, metric) }, {}, { debounced: true });
    };

    // Primary and secondary metrics are typed in and saved on the test's plans
    const plans = testData?.plans || {};
    const validatePrimaryFormat = useMemo(() => createValidator(primaryMetricFormatSchema), []);

    const handlePrimaryMetricChange = (e) => {
        if (!canUpdate) return;
        const val = e.target.value;
        validateFieldValue('primaryMetric', val, validatePrimaryFormat);
        setTestData((prev) => ({ ...prev, plans: { ...(prev?.plans || {}), primary_metric: val } }));
        saveLater(
            'plans.primary_metric',
            (test) => ({ plans: { primary_metric: test?.plans?.primary_metric ?? '' } }),
            (test) => validatePrimaryFormat(test?.plans?.primary_metric ?? '')
        );
    };

    const handlePrimaryMetricBlur = () => {
        if (!canUpdate) return;
        flush('plans.primary_metric');
    };

    const [secondaryMetricInput, setSecondaryMetricInput] = useState('');

    const secondaryMetricsList = useMemo(
        () => toSecondaryMetricsList(plans.secondary_metric),
        [plans.secondary_metric]
    );

    const saveSecondaryMetrics = (updatedList) => {
        setTestData((prev) => ({
            ...prev,
            plans: {
                ...(prev?.plans || {}),
                secondary_metric: updatedList,
            },
        }));
        saveNow({ plans: { secondary_metric: updatedList } }, 'plans.secondary_metric');
    };

    const handleAddSecondaryMetric = () => {
        const text = secondaryMetricInput.trim();
        try {
            secondaryMetricItemSchema.validateSync(text);
        } catch (err) {
            toast.error(err.message || 'Invalid secondary metric.');
            return;
        }

        if (secondaryMetricsList.length >= EPM_LIMITS.SECONDARY_METRICS_MAX_COUNT) {
            toast.error(`Maximum ${EPM_LIMITS.SECONDARY_METRICS_MAX_COUNT} secondary metrics allowed.`);
            return;
        }

        const primaryNormalized = (plans.primary_metric || '').trim().toLowerCase();
        if (primaryNormalized && text.toLowerCase() === primaryNormalized) {
            toast.error('Secondary metric cannot be identical to the primary metric.');
            return;
        }

        if (secondaryMetricsList.some((sm) => sm.toLowerCase() === text.toLowerCase())) {
            toast.error(`Secondary metric "${text}" is already added.`);
            return;
        }

        saveSecondaryMetrics([...secondaryMetricsList, text]);
        setSecondaryMetricInput('');
    };

    const handleRemoveSecondaryMetric = (indexToRemove) => {
        saveSecondaryMetrics(secondaryMetricsList.filter((_, idx) => idx !== indexToRemove));
    };

    const changeMetric = (name) => {
        const chosen = findMetric(name) || manualMetric(name, savedPlan);
        setPrimaryMetric(chosen.name);
        savePlan({ primary_metric: chosen.name, duration_days: daysFor(effect, chosen) });
    };

    // Location values of the current test
    const currentLocation = useMemo(() => {
        return testData?.ideation?.location || testData?.location || {};
    }, [testData]);

    // Active conflict-enabled location field definitions (excluding None)
    const activeLocationFields = useMemo(() => {
        return (testLocations || []).filter((loc) => {
            const role = (loc?.conflict_role || '').trim().toLowerCase();
            return role === 'hard' || role === 'soft';
        });
    }, [testLocations]);

    // Compare current test against all other tests based on location fields
    const conflicts = useMemo(() => {
        const currentId = String(testId(testData) || testData?.key || '');
        if (!activeLocationFields.length) return [];

        const results = [];

        (tests || []).forEach((otherTest) => {
            if (otherTest === testData) return;
            const otherId = String(testId(otherTest) || otherTest?.key || '');
            if (currentId && otherId && currentId === otherId) return;

            const otherLocation = otherTest?.ideation?.location || otherTest?.location || {};
            const matches = findLocationFieldMatches(currentLocation, otherLocation, activeLocationFields);

            if (matches.length > 0) {
                const hasHardMatch = matches.some(
                    (m) => (m.role || '').trim().toLowerCase() === 'hard'
                );
                results.push({
                    test: otherTest,
                    role: hasHardMatch ? 'Hard' : 'Soft',
                    matches,
                });
            }
        });

        return results.sort((a, b) => {
            if (a.role === 'Hard' && b.role !== 'Hard') return -1;
            if (a.role !== 'Hard' && b.role === 'Hard') return 1;
            return 0;
        });
    }, [tests, testData, currentLocation, activeLocationFields]);

    const hardConflicts = useMemo(
        () => conflicts.filter((c) => c.role === 'Hard'),
        [conflicts]
    );
    const softConflicts = useMemo(
        () => conflicts.filter((c) => c.role === 'Soft'),
        [conflicts]
    );

    const hasHardConflict = hardConflicts.length > 0 && !conflictResolution;

    // Resolving clears the card's "conflict" flag; undo puts it back
    const resolveConflict = (resolution) => {
        setConflictResolution(resolution);
        savePlan({ conflict_resolution: resolution }, { flag: resolution ? null : 'conflict' });
    };

    return (
        <div className="epm-plan">
            <header className="epm-workflow-step__heading epm-plan__heading">
                <span>03</span>
                <div>
                    <h2>Plan</h2>
                    <p>Define how the test will be measured, powered, and safely delivered.</p>
                </div>
            </header>

            <section className="epm-plan__card relative">
                <div className="epm-plan__section-heading"><h3>What decides it</h3></div>
                <div className="epm-plan__primary-metric gap-y-3">
                    <span className="uppercase tracking-[.08em] font-bold text-[10px]">Primary <Lock size={11} className="inline ml-1" /></span>
                    <div className="flex flex-col gap-1 w-full">
                        <div className="flex items-center gap-2">
                            <Input
                                id="epm-field-primary-metric"
                                disabled={!canUpdate}
                                error={fieldErrors.primaryMetric}
                                value={plans.primary_metric || ''}
                                onChange={handlePrimaryMetricChange}
                                onBlur={handlePrimaryMetricBlur}
                                placeholder="e.g. Add-to-basket rate"
                                aria-label="Primary metric"
                                className="flex-1"
                            />
                            <EpmCharCounter current={plans.primary_metric} max={EPM_LIMITS.PRIMARY_METRIC_MAX} />
                        </div>
                    </div>
                    <EpmTooltip content="Only one metric decides the verdict. With three primaries at α 0.05, the chance that one wins by luck alone is about 14%. Report everything, decide on one. Frozen at go-live." />

                    <span className="uppercase tracking-[.08em] font-bold text-[10px] self-start mt-2.5">Secondary</span>
                    <div className="flex flex-col gap-2 min-w-0" id="epm-field-secondary-metric">
                        {canUpdate && (
                            secondaryMetricsList.length < MAX_SECONDARY_METRICS ? (
                                <div className="flex items-center gap-2">
                                    <Input
                                        className="flex-1 min-w-0"
                                        value={secondaryMetricInput}
                                        onChange={(e) => setSecondaryMetricInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddSecondaryMetric();
                                            }
                                        }}
                                        placeholder="e.g. Checkout completion rate"
                                        aria-label="Secondary metric"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddSecondaryMetric}
                                        disabled={!secondaryMetricInput.trim()}
                                        className="px-4 py-2 border border-[#cbd3ef] rounded-lg bg-white text-[var(--primary)] text-xs font-semibold hover:bg-slate-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                                    >
                                        Add
                                    </button>
                                </div>
                            ) : (
                                <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-medium">
                                    All secondary metrics added ({MAX_SECONDARY_METRICS}/{MAX_SECONDARY_METRICS} maximum reached).
                                </div>
                            )
                        )}

                        {/* Selected Secondary Metrics List */}
                        {secondaryMetricsList.length > 0 && (
                            <div className="flex flex-col gap-1.5 mt-1">
                                {secondaryMetricsList.map((metricName, index) => (
                                    <div
                                        key={`${index}-${metricName}`}
                                        className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs transition-all hover:bg-slate-100"
                                    >
                                        <span className="text-slate-700 truncate font-normal min-w-0 pr-2">
                                            {metricName}
                                        </span>
                                        {canUpdate && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSecondaryMetric(index)}
                                                className="text-slate-400 hover:text-red-500 transition-colors p-1 flex-none cursor-pointer bg-transparent border-0"
                                                title="Remove secondary metric"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <span className="text-[11px] text-[#8a92a7]">
                            {secondaryMetricsList.length === 0
                                ? `Optional · up to ${MAX_SECONDARY_METRICS} secondary metrics`
                                : `${secondaryMetricsList.length} of ${MAX_SECONDARY_METRICS} secondary metric(s) added`}
                        </span>
                    </div>
                    <div className="self-start mt-2.5">
                        <EpmTooltip content="Secondary metrics are reported alongside the result but never decide the verdict. Up to 5." />
                    </div>
                </div>

                {/* Guardrails and Secondary chips — hidden for now
                <div className="epm-plan__also relative">
                    <label className="text-[10px] text-[#687390] font-bold tracking-[.05em] uppercase flex items-center gap-1.5">Guardrails <EpmTooltip content="Each guardrail carries a tolerance. A breach stops the test immediately, whatever the primary is doing. Up to 5." /></label>
                    <div className="epm-plan__also-chips">
                        <MetricChip>Return rate <b>+1.0pp</b></MetricChip>
                        <MetricChip>PDP load time <b>+150ms</b></MetricChip>
                        <button type="button" className="text-[var(--primary)] text-[10px] bg-transparent border-0 flex items-center gap-1"><Plus size={12} /> Add guardrail</button>
                    </div>

                    <label className="text-[10px] text-[#687390] font-bold tracking-[.05em] uppercase flex items-center gap-1.5">Secondary <EpmTooltip content="Secondary metrics are reported alongside the result but never decide the verdict. Up to 5." /></label>
                    <div className="epm-plan__also-chips">
                        <MetricChip>Checkout completion rate</MetricChip>
                        <MetricChip>Revenue per visit</MetricChip>
                        <button type="button" className="text-[var(--primary)] text-[10px] bg-transparent border-0 flex items-center gap-1"><Plus size={12} /> Add</button>
                    </div>
                </div>
                */}
            </section>

            {/* How long and When it may stop — hidden for now
            <section className="epm-plan__card relative">
                <div className="epm-plan__section-heading"><span>2 / 3</span><h3>How long</h3></div>
                <div className="epm-plan__duration">
                    <strong>+{effect}%</strong>
                    <span className="text-[#8a93a8] text-[11px]">relative</span>
                    <span className="text-[#8a93a8] text-[11px]">→</span>
                    <b className="text-[#26335f] text-2xl">{Number.isFinite(stats.days) ? `${stats.days} days` : 'Planning inputs required'}</b>
                    {stats.rawDays < MIN_RUN_DAYS && <span className="text-[#8a93a8] text-[11px]">7-day minimum run</span>}
                    <em className="ml-auto text-[#7f889f] text-[10px] not-italic">smallest lift worth detecting</em>
                </div>
                <input className="epm-plan__range" type="range" min={MIN_MDE} max={MAX_MDE} step="0.5" value={effect} onChange={(event) => changeEffect(Number(event.target.value))} aria-label="Minimum worthwhile effect" />
                <div className="relative h-3 text-[#9aa2b5] text-[9px]">
                    {SLIDER_LABELS.map((mde) => (
                        <span
                            key={mde}
                            className="absolute"
                            style={{ left: `${sliderPosition(mde)}%`, transform: mde === MIN_MDE ? 'none' : mde === MAX_MDE ? 'translateX(-100%)' : 'translateX(-50%)' }}
                        >
                            {mde}%
                        </span>
                    ))}
                </div>
                <p className="epm-plan__hint mt-4">
                    Choose an effect size based on your observed results and available traffic.
                </p>
                <DurationChart
                    curve={stats.curve}
                    effect={effect}
                    days={stats.rawDays}
                    label={`${effect}% · ${Number.isFinite(stats.days) ? `${stats.days} days` : 'Planning inputs required'}${stats.rawDays < MIN_RUN_DAYS ? ' (minimum run)' : ''}`}
                />
                <p className="epm-plan__hint">Half the lift takes four times as long <EpmTooltip content="Days scale with 1/MDE². Halving the smallest lift you want to detect needs four times the sample, so four times the calendar at the same traffic. The dashed line is the org's four-week viability limit." /></p>

                <div className="epm-plan__collapsible flat">
                    <div className={`epm-plan__collapsible-header relative ${baselineOpen ? 'is-open' : ''}`} onClick={() => setBaselineOpen(!baselineOpen)}>
                        <ChevronDown size={17} />
                        <span className="epm-plan__collapsible-title">Sample size & baseline</span>
                        <span className="epm-plan__collapsible-subtitle">{formatNumber(stats.perVariant)} per variant · baseline {metric.display} ({metric.source}, {metric.verified ? 'verified' : 'unverified'}) · {alpha} · {POWER_OPTIONS[power] * 100}%</span>
                        <span className="epm-plan__collapsible-action">{baselineOpen ? 'Hide' : 'Show'}</span>
                    </div>
                    {baselineOpen && (
                        <div className="epm-plan__collapsible-body">
                            <div className="grid grid-cols-[130px_1fr] gap-x-4 gap-y-[9px] text-[10px]">
                                <dt className="text-[#8992a8]">Baseline rate</dt>
                                <dd className="m-0 text-[#4d5873]">
                                    {metric.display} · {formatNumber(metric.dailyTraffic)} eligible / day · last 4 weeks
                                    <EpmTooltip content="No measurement adapter? The baseline is entered by hand and flagged unverified, and the flag carries through to the results." />
                                    <div className="text-[#8992a8] mt-1">{metric.verified ? `From ${metric.source} adapter, verified` : 'Entered by hand, unverified'} · <button type="button" className="text-[var(--primary)] bg-transparent border-0 p-0 cursor-pointer hover:underline">Refresh</button></div>
                                </dd>
                                <dt className="text-[#8992a8]">Sample</dt>
                                <dd className="m-0 text-[#4d5873]">{formatNumber(stats.perVariant)} per variant · {formatNumber(stats.perVariant * 2)} total</dd>
                                <dt className="text-[#8992a8]">More variants</dt>
                                <dd className="m-0 text-[#4d5873]">A third arm raises the horizon to approximately {stats.thirdArmDays} days.</dd>
                            </div>

                            <div className="epm-plan__collapsible flat mt-3 pt-3 border-t border-[#edf0f6]">
                                <div className={`epm-plan__collapsible-header ${overridesOpen ? 'is-open' : ''}`} onClick={() => setOverridesOpen(!overridesOpen)}>
                                    <span className="epm-plan__collapsible-title">α · power · tails</span>
                                    <span className="epm-plan__collapsible-subtitle">{alpha} · {power} · {tails} · org defaults</span>
                                    <span className="epm-plan__collapsible-action">{overridesOpen ? 'Hide' : 'Override'}</span>
                                </div>
                                {overridesOpen && (
                                    <div className="epm-plan__collapsible-body">
                                        <div className="epm-plan__overrides">
                                            <Select value={alpha} onChange={e => setAlpha(e.target.value)} options={ALPHA_SELECT_OPTIONS} selectClassName="text-[11px] py-2" />
                                            <Select value={power} onChange={e => setPower(e.target.value)} options={POWER_SELECT_OPTIONS} selectClassName="text-[11px] py-2" />
                                            <Select value={tails} onChange={e => setTails(e.target.value)} options={TAILS_SELECT_OPTIONS} selectClassName="text-[11px] py-2" />
                                        </div>
                                        <p className="epm-plan__hint">Overrides are recorded on the test and shown with its results.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="epm-plan__card relative">
                <div className="epm-plan__section-heading">
                    <span>3 / 3</span>
                    <h3>When it may stop <Lock size={13} className="inline ml-1 align-text-top" /></h3>
                    <EpmTooltip content="Peeking at a fixed test raises false wins from 5% to roughly 20–30%. So a fixed test shows significance during the run but marks it not a valid stopping point, and its known end date is what the scheduler books. Sequential uses always-valid p-values (mSPRT)." />
                </div>
                <div className="epm-plan__stop-options">
                    <button className={stopRule === 'fixed' ? 'is-active' : ''} onClick={() => { setStopRule('fixed'); savePlan({ stop_rule: 'fixed' }); }} type="button">
                        <span className="radio" />
                        <strong>Fixed horizon</strong><em>default</em>
                        <p>Runs the full {Number.isFinite(stats.days) ? `${stats.days} days` : 'Planning inputs required'} and is analysed once. Always valid.</p>
                    </button>
                    <button className={stopRule === 'sequential' ? 'is-active' : ''} onClick={() => { setStopRule('sequential'); savePlan({ stop_rule: 'sequential' }); }} type="button">
                        <span className="radio" />
                        <strong>Sequential</strong>
                        <p>Watched against a pre-set boundary; may stop early on a real effect.</p>
                    </button>
                </div>
            </section>
            */}

            <div className="flex flex-col gap-4 mt-6">
                <section className={`epm-idea__card mb-0 ${hasHardConflict ? 'epm-plan__conflicts' : ''}`}>
                    <button
                        className="epm-idea__disclosure"
                        type="button"
                        onClick={() => setConflictsOpen(!conflictsOpen)}
                        aria-expanded={conflictsOpen}
                    >
                        <ChevronDown className={conflictsOpen ? '' : '-rotate-90'} size={17} />
                        <strong>Conflicts</strong>
                        <span>
                            {conflicts.length
                                ? `${conflicts.length} found · ${hardConflicts.length} hard, ${softConflicts.length} soft`
                                : 'None found · no location conflicts detected'}
                        </span>
                        <em>{conflictsOpen ? 'Hide' : 'Show'}</em>
                    </button>
                    {conflictsOpen && (
                        conflicts.length > 0 ? (
                            <div className="epm-idea__past-tests">
                                {conflicts.map(({ test, role, matches }) => (
                                    <article key={String(testId(test) || test?.key)}>
                                        <div>
                                            <p><strong>{formatTestCardId(test)}</strong> {test?.name || 'Untitled test'}</p>
                                            <small>{matches.map((m) => `${m.label}: ${m.value}`).join(' · ')}</small>
                                        </div>
                                        <span className={role === 'Hard' ? 'is-hard' : 'is-soft'}>{role}</span>
                                    </article>
                                ))}
                                <p className="epm-idea__note">
                                    Hard conflicts block the gate until resolved; soft conflicts indicate potential interaction risk.
                                </p>
                            </div>
                        ) : (
                            <div className="mt-3 pl-7 flex items-center gap-2 text-[12px] text-[#496577]">
                                <Check size={14} className="text-[#2f9d69]" />
                                <span>No other tests share location fields with this test.</span>
                            </div>
                        )
                    )}
                </section>

            </div>

        </div>
    );
};

export default PlanStep;
